import React, { Component } from 'react';
import { Table, Select, Input, DatePicker, Button, Modal, Descriptions, Pagination, Tooltip, Segmented } from 'antd';
import { History, Search, Eye, UserRound, FilterX, RotateCw, Plus, Pencil, LogIn, ClipboardCheck, ArrowRight } from 'lucide-react';
import Inherit from '../../core/inherit';
import ActivityLogsController from './activityLogs.controller';
import withNavigate from '../../core/withNavigate';
import {
    Layout,
    AnalyticsSkeleton,
    CardSkeleton,
    PageHeader,
    StatCard,
    SectionCard,
    FilterBar,
    FilterField,
    EmptyState,
    IconAction,
    StatusTag,
    tone
} from '../common';
import { ActionTag, UserTypeTag, EntityIcon } from './elements/ActivityTags';
import dayjs from 'dayjs';
import { brand } from '../../theme/tokens';
import { t } from '../../company/labels';
import { isUserTypeEnabled } from '../../company/session';

// "Tipo de usuario" filter; only the types the signed-in company's backend has are offered.
const USER_TYPE_FILTERS = [
    { value: 'admin', label: 'Admin' },
    { value: 'gerente', label: 'Gerente' },
    { value: 'asesor', label: 'Asesor' },
    { value: 'promotor', label: t('promotor') },
    { value: 'auditor', label: 'Auditor' },
    { value: 'location', label: 'Ubicación' }
];

const { RangePicker } = DatePicker;

// Quick date presets
const datePresets = [
    { label: 'Hoy', value: 'today' },
    { label: 'Ayer', value: 'yesterday' },
    { label: 'Últimos 7 días', value: 'week' },
    { label: 'Últimos 30 días', value: 'month' },
    { label: 'Personalizado', value: 'custom' },
];

/** JSON snapshot block for the detail modal (before / after). */
const DataBlock = ({ title, tone: toneName, data }) => (
    <div className="min-w-0">
        <h3 className="m-0 mb-2 flex items-center gap-2 text-sm font-semibold text-surface-900">
            <span className={`h-2 w-2 rounded-full ${tone(toneName).solid}`} />
            {title}
        </h3>
        <pre className="m-0 max-h-64 overflow-auto rounded-lg border border-surface-200 bg-surface-50 p-3 font-mono text-xs leading-relaxed text-surface-700">
            {JSON.stringify(data, null, 2)}
        </pre>
    </div>
);

class ShelfActivityLogs extends Inherit(ActivityLogsController, Component) {
    state = {
        loading: true,
        logs: [],
        stats: null,
        pagination: {},
        availableTags: [],
        filterAction: 'all',
        filterEntity: 'all',
        filterUserType: 'all',
        filterUser: 'all',
        filterTag: 'all',
        datePreset: 'week',
        startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        search: '',
        page: 1,
        selectedLog: null,
        showDetailModal: false,
        showCustomDate: false,
    };

    getActionTag(action, log) {
        return (
            <ActionTag
                label={this.getActionLabel(action)}
                tone={this.getActionTone(action)}
                isActivation={this.isActivation(log)}
                isDeactivation={this.isDeactivation(log)}
            />
        );
    }

    getEntityIcon(entityType) {
        return <EntityIcon type={entityType} />;
    }

    getUserTypeTag(userType) {
        return <UserTypeTag userType={userType} tone={this.getUserTypeTone(userType)} />;
    }

    renderPeriod() {
        const { datePreset, showCustomDate, startDate, endDate } = this.state;

        return (
            <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-surface-600">Período:</span>
                <Segmented value={datePreset} onChange={(preset) => this.handleDatePresetChange(preset)} options={datePresets} />
                {showCustomDate && (
                    <RangePicker
                        value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
                        onChange={(dates) => this.setCustomDateRange(dates)}
                        format="DD/MM/YYYY"
                        placeholder={['Fecha inicio', 'Fecha fin']}
                    />
                )}
            </div>
        );
    }

    renderStats() {
        const { stats } = this.state;
        if (!stats) return null;

        return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <StatCard label="Total de Registros" value={(stats.totalLogs || 0).toLocaleString()} icon={<History />} tone="brand" hint="Con los filtros actuales" />
                <StatCard label="Creaciones" value={(stats.actionStats?.CREATE || 0).toLocaleString()} icon={<Plus />} tone="success" />
                <StatCard label="Actualizaciones" value={(stats.actionStats?.UPDATE || 0).toLocaleString()} icon={<Pencil />} tone="warning" />
                <StatCard label="Inicios de sesión" value={(stats.actionStats?.LOGIN || 0).toLocaleString()} icon={<LogIn />} tone="violet" />
                <StatCard label="Store Checks" value={(stats.entityTypeStats?.STORE_CHECK || 0).toLocaleString()} icon={<ClipboardCheck />} tone="info" />
            </div>
        );
    }

    renderFilters() {
        const { filterAction, filterEntity, filterUserType, filterTag, availableTags, search } = this.state;

        return (
            <FilterBar
                className="!mb-0"
                actions={
                    <Button icon={<FilterX />} onClick={() => this.clearFilters()}>
                        Limpiar
                    </Button>
                }>
                <FilterField label="Buscar" width={280}>
                    <Input
                        placeholder="Buscar por usuario, entidad o descripción..."
                        prefix={<Search className="text-surface-400" />}
                        value={search}
                        onChange={(e) => this.setSearch(e.target.value)}
                        allowClear
                    />
                </FilterField>
                <FilterField label="Acción" width={170}>
                    <Select value={filterAction} onChange={(value) => this.setFilterAction(value)} placeholder="Acción">
                        <Select.Option value="all">Todas las acciones</Select.Option>
                        <Select.Option value="CREATE">Crear</Select.Option>
                        <Select.Option value="UPDATE">Actualizar</Select.Option>
                        <Select.Option value="LOGIN">Iniciar sesión</Select.Option>
                        <Select.Option value="LOGOUT">Cerrar sesión</Select.Option>
                    </Select>
                </FilterField>
                <FilterField label="Entidad" width={180}>
                    <Select value={filterEntity} onChange={(value) => this.setFilterEntity(value)} placeholder="Entidad">
                        <Select.Option value="all">Todas las entidades</Select.Option>
                        <Select.Option value="STORE_CHECK">Store Checks</Select.Option>
                        <Select.Option value="CLIENT">Clientes</Select.Option>
                        <Select.Option value="STORE">Tiendas</Select.Option>
                        <Select.Option value="USER">Usuarios</Select.Option>
                        <Select.Option value="PRODUCT">Productos</Select.Option>
                        <Select.Option value="PRICE">Precios</Select.Option>
                        <Select.Option value="STORETYPE">Tipos de tienda</Select.Option>
                        <Select.Option value="CAMPAIGN">Campañas</Select.Option>
                        <Select.Option value="CONTRACT">Contratos</Select.Option>
                        <Select.Option value="TAG">Etiquetas</Select.Option>
                        <Select.Option value="NOTIFICATION">Notificaciones</Select.Option>
                        <Select.Option value="PERMISSION">Permisos</Select.Option>
                    </Select>
                </FilterField>
                <FilterField label="Tipo de usuario" width={170}>
                    <Select value={filterUserType} onChange={(value) => this.setFilterUserType(value)} placeholder="Tipo de usuario">
                        <Select.Option value="all">Todos los tipos</Select.Option>
                        {USER_TYPE_FILTERS.filter((option) => isUserTypeEnabled(option.value)).map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </FilterField>
                <FilterField label="Etiqueta" width={190}>
                    <Select
                        value={filterTag}
                        onChange={(value) => this.setFilterTag(value)}
                        placeholder="Etiqueta"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }>
                        <Select.Option value="all">Todas las etiquetas</Select.Option>
                        {availableTags.map((tag) => (
                            <Select.Option key={tag._id} value={tag._id} label={tag.name}>
                                <div className="flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color || brand[500] }} />
                                    {tag.name}
                                </div>
                            </Select.Option>
                        ))}
                    </Select>
                </FilterField>
            </FilterBar>
        );
    }

    get columns() {
        return [
            {
                title: 'Fecha',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 150,
                render: (date) => (
                    <Tooltip title={this.formatDate(date)}>
                        <span className="text-sm text-surface-600 tabular-nums">{this.formatRelativeTime(date)}</span>
                    </Tooltip>
                ),
            },
            {
                title: 'Usuario',
                key: 'user',
                width: 230,
                render: (_, record) => (
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                            <UserRound className="text-sm" />
                        </span>
                        <div className="min-w-0">
                            <div className="truncate font-medium text-surface-900">{record.userName || 'Sistema'}</div>
                            <div className="truncate text-xs text-surface-500">{record.userEmail || '-'}</div>
                        </div>
                    </div>
                ),
            },
            {
                title: 'Tipo',
                dataIndex: 'userType',
                key: 'userType',
                width: 130,
                render: (userType) => userType ? this.getUserTypeTag(userType) : <StatusTag>Sistema</StatusTag>,
            },
            {
                title: 'Acción',
                dataIndex: 'action',
                key: 'action',
                width: 140,
                render: (action, record) => this.getActionTag(action, record),
            },
            {
                title: 'Entidad',
                key: 'entity',
                width: 160,
                render: (_, record) => (
                    <div className="flex items-center gap-2 text-sm text-surface-700">
                        <span className="text-surface-400">{this.getEntityIcon(record.entityType)}</span>
                        <span>{this.getEntityLabel(record.entityType)}</span>
                    </div>
                ),
            },
            {
                title: 'Descripción',
                key: 'description',
                render: (_, record) => (
                    <div>
                        <div className="text-sm text-surface-900">{this.getLogDescription(record)}</div>
                        {record.changedFields && record.changedFields.length > 0 && (
                            <div className="mt-1 text-xs text-surface-500">
                                Campos: {record.changedFields.slice(0, 3).join(', ')}
                                {record.changedFields.length > 3 ? ` +${record.changedFields.length - 3} más` : ''}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                title: '',
                key: 'actions',
                width: 56,
                align: 'right',
                render: (_, record) => <IconAction icon={<Eye />} label="Ver detalle" onClick={() => this.viewLogDetails(record._id)} />,
            },
        ];
    }

    renderTable() {
        const { logs, pagination } = this.state;

        return (
            <>
                <Table dataSource={logs} columns={this.columns} rowKey="_id" pagination={false} size="middle" scroll={{ x: 960 }} />
                {pagination.total > 0 && (
                    <div className="flex flex-col gap-2 border-t border-surface-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-sm text-surface-500 tabular-nums">
                            Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} registros
                        </span>
                        <Pagination
                            current={pagination.page || 1}
                            total={pagination.total || 0}
                            pageSize={pagination.limit || 50}
                            onChange={(page) => this.setPage(page)}
                            showSizeChanger={false}
                            size="small"
                        />
                    </div>
                )}
            </>
        );
    }

    renderDetailModal() {
        const { selectedLog, showDetailModal } = this.state;

        if (!selectedLog) return null;

        return (
            <Modal
                title="Detalle del registro de actividad"
                open={showDetailModal}
                onCancel={() => this.closeDetailModal()}
                footer={[
                    <Button key="close" onClick={() => this.closeDetailModal()}>
                        Cerrar
                    </Button>
                ]}
                width={selectedLog.previousData && selectedLog.newData ? 900 : 700}>
                <Descriptions bordered column={1} size="small" className="mt-2">
                    <Descriptions.Item label="Fecha">
                        <span className="tabular-nums">{this.formatDate(selectedLog.createdAt)}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Usuario">
                        {selectedLog.userName || 'Sistema'} <span className="text-surface-500">({selectedLog.userEmail || '-'})</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tipo de usuario">
                        {selectedLog.userType ? this.getUserTypeTag(selectedLog.userType) : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Acción">
                        {this.getActionTag(selectedLog.action, selectedLog)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Entidad">
                        <span className="inline-flex items-center gap-2">
                            <span className="text-surface-400">{this.getEntityIcon(selectedLog.entityType)}</span>
                            {this.getEntityLabel(selectedLog.entityType)}
                        </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Nombre de entidad">
                        {selectedLog.entityName || selectedLog.entityId || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Descripción">
                        {selectedLog.description || this.getLogDescription(selectedLog)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Campos modificados">
                        {selectedLog.changedFields?.length ? (
                            <div className="flex flex-wrap gap-1">
                                {selectedLog.changedFields.map((f) => (
                                    <StatusTag key={f} tone="brand">
                                        {f}
                                    </StatusTag>
                                ))}
                            </div>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="IP">
                        <span className="font-mono text-xs">{selectedLog.ipAddress || '-'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Ruta">
                        <span className="font-mono text-xs">
                            {selectedLog.requestMethod} {selectedLog.requestPath || '-'}
                        </span>
                    </Descriptions.Item>
                </Descriptions>

                {(selectedLog.previousData || selectedLog.newData) && (
                    <div className={`mt-5 grid gap-4 ${selectedLog.previousData && selectedLog.newData ? 'md:grid-cols-[1fr_auto_1fr]' : ''}`}>
                        {selectedLog.previousData && <DataBlock title="Datos anteriores" tone="danger" data={selectedLog.previousData} />}
                        {selectedLog.previousData && selectedLog.newData && (
                            <div className="hidden items-center text-surface-300 md:flex">
                                <ArrowRight />
                            </div>
                        )}
                        {selectedLog.newData && <DataBlock title="Datos nuevos" tone="success" data={selectedLog.newData} />}
                    </div>
                )}
            </Modal>
        );
    }

    render() {
        const { loading, logs, pagination } = this.state;

        return (
            <Layout current="activity-logs" user={this.user}>
                <PageHeader
                    icon={<History />}
                    title="Registro de Actividad"
                    description="Historial de todas las acciones realizadas en el sistema"
                    actions={
                        <Button icon={<RotateCw />} onClick={() => this.refreshLogs()}>
                            Actualizar
                        </Button>
                    }>
                    {this.renderPeriod()}
                </PageHeader>

                {loading ? (
                    <div className="flex flex-col gap-y-6">
                        <AnalyticsSkeleton cards={5} />
                        <CardSkeleton rows={10} />
                    </div>
                ) : (
                    <div className="flex flex-col gap-y-4 animate-in">
                        {this.renderStats()}

                        {this.renderFilters()}

                        <SectionCard
                            flush
                            icon={<History />}
                            title="Registros"
                            extra={pagination.total > 0 ? <StatusTag className="tabular-nums">{pagination.total.toLocaleString()} registros</StatusTag> : null}>
                            {logs.length === 0 ? (
                                <EmptyState
                                    icon={<History />}
                                    title="No hay registros de actividad"
                                    description="Los registros aparecerán aquí cuando se realicen acciones en el sistema"
                                />
                            ) : (
                                this.renderTable()
                            )}
                        </SectionCard>
                    </div>
                )}

                {this.renderDetailModal()}
            </Layout>
        );
    }
}

export default withNavigate(ShelfActivityLogs);
